import { BillingApi } from "@/lib/api/billing-api";
import { Button } from "@/lib/components/Button/Button";
import { Input } from "@/lib/components/Input";
import Toast from "@/lib/components/Toast";
import { CouponDto } from "@repo/api/coupon";
import { IconCheck } from "@tabler/icons-react";
import { useState } from "react";

type Props = {
  autoApplyCoupon: CouponDto | null;
  currentCoupon: CouponDto | null;
  teamExternalId: string;
  setCurrentCoupon: (coupon: CouponDto | null) => void;
};

export default function CouponCodeInput({
  autoApplyCoupon,
  currentCoupon,
  teamExternalId,
  setCurrentCoupon,
}: Props) {
  const initialCode = getInitialCode(autoApplyCoupon, currentCoupon);

  const [couponCode, setCouponCode] = useState<string>(initialCode);
  const [viewState, setViewState] = useState<"INITIAL" | "INPUT" | "APPLIED">(
    initialCode ? "APPLIED" : "INITIAL",
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleApplyCoupon = async () => {
    if (isLoading || !couponCode) {
      return;
    }

    setIsLoading(true);
    try {
      const coupon = await BillingApi.getCoupon(teamExternalId, couponCode);
      setCurrentCoupon(coupon);
      setViewState("APPLIED");
      Toast.info("Coupon applied successfully");
    } catch (error) {
      console.error(error);
      Toast.error("No coupon found");
    } finally {
      setIsLoading(false);
    }
  };

  if (viewState === "INITIAL") {
    return (
      <p
        className="cursor-pointer text-sm text-text-secondary underline"
        onClick={() => setViewState("INPUT")}
      >
        Do you have a coupon?
      </p>
    );
  }

  if (viewState === "APPLIED") {
    return (
      <p
        className="text-sm text-text-secondary"
        onClick={() => setViewState("INPUT")}
      >
        Coupon applied: {couponCode}
      </p>
    );
  }

  return (
    <div className="flex gap-2">
      <Input
        containerSize="sm"
        value={couponCode}
        onChange={(e) => setCouponCode(e.target.value)}
        placeholder="Coupon code"
        disabled={isLoading}
      />
      <Button
        size="iconSm"
        variant="outline"
        onClick={handleApplyCoupon}
        isLoading={isLoading}
        disabled={isLoading || !couponCode}
        className="shrink-0 grow-0 basis-auto"
      >
        <IconCheck />
      </Button>
    </div>
  );
}

function getInitialCode(
  autoApplyCoupon: CouponDto | null,
  currentCoupon: CouponDto | null,
): string {
  if (!currentCoupon) {
    return "";
  }

  if (autoApplyCoupon && autoApplyCoupon.code === currentCoupon.code) {
    return "";
  }

  return currentCoupon.code;
}
